import { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button } from '@/components/Button';
import { PWA } from '@/lib/glossario';

/**
 * Aviso gentil de "Nova versão disponível" (PWA).
 *
 * Montar este componente também REGISTRA o service worker (usamos `injectRegister: null`
 * no vite-plugin-pwa; o registro é feito aqui pelo `useRegisterSW`). Em dev o registro é
 * um no-op (`devOptions.enabled: false`).
 *
 * Comportamento (registerType: 'prompt'): quando há uma versão nova esperando, mostra um
 * banner discreto no rodapé; o usuário decide quando atualizar — assim não perdemos dados
 * de um formulário aberto. `Esc` equivale a "Agora não". Acessível (`aria-live="polite"`).
 */
export function AtualizacaoDisponivel() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  useEffect(() => {
    if (!needRefresh) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNeedRefresh(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [needRefresh, setNeedRefresh]);

  if (!needRefresh) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
    >
      <div className="flex w-full max-w-md animate-surgir flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-cartao-alto sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">{PWA.atualizacaoTitulo}</p>
          <p className="mt-0.5 text-xs text-slate-600">{PWA.atualizacaoTexto}</p>
        </div>
        <div className="flex shrink-0 gap-2 sm:flex-col">
          <Button size="sm" onClick={() => void updateServiceWorker(true)}>
            {PWA.atualizar}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setNeedRefresh(false)}>
            {PWA.agoraNao}
          </Button>
        </div>
      </div>
    </div>
  );
}
